import { Menu, MenuItem } from "@mui/material";
import { DotsThreeVerticalIcon } from "@phosphor-icons/react";
import { useId, useState } from "react";
import { useIntl } from "react-intl";

import { IconButton } from "@/components/IconButton/IconButton";
import { rudderAnalytics, CommonTrackingEvents } from "@/lib/rudderstack";
import { type Traveller } from "@/types/types";

type Props = {
  traveller: Traveller;
  onDelete: (traveller: Traveller) => void;
  onEdit: (traveller: Traveller) => void;
};

export function TravellerActions({ traveller, onDelete, onEdit }: Props) {
  const intl = useIntl();
  const buttonId = useId();
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const onDeleteClick = (event: React.MouseEvent<HTMLElement>) => {
    rudderAnalytics.track(CommonTrackingEvents.USER_DELETED_TRAVELER);
    onDelete(traveller);
    handleClose(event);
  };

  const onEditClick = (event: React.MouseEvent<HTMLElement>) => {
    rudderAnalytics.track(CommonTrackingEvents.USER_ENTERED_EDITING_TRAVELER);
    onEdit(traveller);
    handleClose(event);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        aria-label={intl.formatMessage({
          defaultMessage: "Actions",
          description: "travellers.actions",
        })}
        id={buttonId}
        aria-controls={open ? menuId : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
      >
        <DotsThreeVerticalIcon size={24} />
      </IconButton>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            dense: true,
            "aria-labelledby": buttonId,
          },
        }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "var(--radius-2xl)",
            boxShadow:
              "0px 1px 8px 0px rgba(56, 56, 81, 0.04), 0px 4px 10px 0px rgba(56, 56, 81, 0.04)",
          },
        }}
      >
        <MenuItem onClick={onEditClick}>
          <span className="text-sm font-medium">
            {intl.formatMessage({
              defaultMessage: "Edit",
              description: "edit",
            })}
          </span>
        </MenuItem>

        <MenuItem onClick={onDeleteClick}>
          <span className="text-sm font-medium text-semantic-error-strong">
            {intl.formatMessage({
              defaultMessage: "Delete",
              description: "delete",
            })}
          </span>
        </MenuItem>
      </Menu>
    </>
  );
}
